import "@blockdaemon/widget-host/global.css";
import "@blockdaemon/widget-host/polyfills";
import {
  getColorSchemeOverride,
  queryClient,
  runWidgetBootstrap,
  WidgetApiError,
} from "@blockdaemon/widget-host";
import { WidgetAppProviders } from "@blockdaemon/widget-host/providers";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import packageJson from "../package.json";
import { cacheTokenizationConfig, fetchTokenizationConfig } from "./config-loader";
import { createTokenizationRouter } from "./create-tokenization-router";
import { messages as enMessages } from "./locales/en-US/messages.mjs";
import { messages as jaMessages } from "./locales/ja-JP/messages.mjs";
import { messages as koMessages } from "./locales/ko-KR/messages.mjs";

async function preloadConfig() {
  try {
    cacheTokenizationConfig(await fetchTokenizationConfig());
  } catch (error) {
    if (error instanceof WidgetApiError && error.status === 401) {
      return;
    }
    console.error("Failed to preload tokenization widget config:", error);
  }
}

await runWidgetBootstrap({
  packageVersion: packageJson.version,
  appDisplayName: "Blockdaemon Tokenization Widget",
  i18nCatalogs: {
    "en-US": enMessages,
    "ko-KR": koMessages,
    "ja-JP": jaMessages,
  },
  preload: preloadConfig,
});

const router = createTokenizationRouter();

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

async function startApp() {
  const rootElement = document.getElementById("root");

  if (!rootElement) {
    throw new Error("Root element not found");
  }

  const colorSchemeOverride = getColorSchemeOverride();

  const root = createRoot(rootElement);

  root.render(
    <StrictMode>
      <WidgetAppProviders queryClient={queryClient} forceColorScheme={colorSchemeOverride}>
        <RouterProvider router={router} />
      </WidgetAppProviders>
    </StrictMode>,
  );
}

void startApp();
